import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { Drawer } from 'vaul'

import { Flex } from '../Flex'
import { Headline2 } from '../Typography'

import { Close } from '@/assets/icons'
import { colors } from '@/constants'
import { ModalComponentProps } from '@/typings/Modals'

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const StyledOverlay = styled(Drawer.Overlay)`
  background-color: rgba(0, 0, 0, 0.5);
  position: fixed;
  inset: 0;
  z-index: 1000;
  animation: ${fadeIn} 250ms cubic-bezier(0.16, 1, 0.3, 1);
`

const StyledContent = styled(Drawer.Content)`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${colors.darkBlue1};
  padding: 1.25rem 1rem;
  border-top-left-radius: 1rem;
  border-top-right-radius: 1rem;
  z-index: 1001;
  outline: none;
  width: 100dvw;
  max-height: 100dvh;
  box-shadow: 0px -10px 38px -10px rgba(22, 23, 24, 0.35);

  &:focus {
    outline: none;
  }
`

const CloseButton = styled(Drawer.Close)`
  display: flex;
  justify-content: flex-end;
  cursor: pointer;
  margin-bottom: 0.625rem;
  opacity: 0.8;
  animation: ${fadeIn} 300ms 150ms backwards cubic-bezier(0.16, 1, 0.3, 1);
  transition: opacity 0.2s ease;
  color: #191c2c;

  &:hover {
    opacity: 1;
  }

  &:focus-visible {
    color: ${colors.blue};
  }
`

const ModalTitle = styled(Drawer.Title)`
  width: 100%;
  text-align: center;
  justify-content: center;
  animation: ${slideUp} 300ms 200ms backwards cubic-bezier(0.16, 1, 0.3, 1);
`

const BodyWrapper = styled(Flex)`
  position: relative;
  flex-direction: column;
  gap: 1rem;
  max-height: 80dvh;
  width: 100%;
  overflow-y: auto;
  animation: ${slideUp} 300ms 250ms backwards cubic-bezier(0.16, 1, 0.3, 1);
`

const FooterWrapper = styled(Flex)`
  margin-top: 1.25rem;
  margin-bottom: var(--safe-area-bottom);
  width: 100%;
  animation: ${slideUp} 300ms 300ms backwards cubic-bezier(0.16, 1, 0.3, 1);
`

export default function MobileModal({
  open,
  setOpen,
  minHeight = '35%',
  modalHeader,
  modalBody,
  modalFooter,
  ...footerProps
}: ModalComponentProps) {
  return (
    <Drawer.Root
      open={open}
      onOpenChange={setOpen}
      repositionInputs={false}
      dismissible
    >
      <Drawer.Portal>
        <Drawer.Description>Modal</Drawer.Description>
        <StyledOverlay
          onClick={(e) => {
            e.stopPropagation()
            setOpen(false)
          }}
        />
        <StyledContent style={{ minHeight }}>
          <CloseButton
            onClick={() => setOpen(false)}
            tabIndex={0}
            role="button"
            aria-label={`Close modal`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setOpen(false)
              }
            }}
          >
            <Close />
          </CloseButton>

          <ModalTitle asChild>
            {typeof modalHeader === 'string' ? (
              <Headline2 style={{ textAlign: 'center' }}>
                {modalHeader}
              </Headline2>
            ) : (
              <Flex style={{ textAlign: 'center' }}>{modalHeader}</Flex>
            )}
          </ModalTitle>

          <BodyWrapper>{modalBody}</BodyWrapper>

          {modalFooter ? (
            <FooterWrapper>{modalFooter(footerProps)}</FooterWrapper>
          ) : null}
        </StyledContent>
      </Drawer.Portal>
    </Drawer.Root>
  )
}
